/**
 * Demo mode — a public, login-free, fully-simulated run of the REAL agency
 * portal. Activated by visiting `/demo`. When active:
 *   - a fake session is seeded into Redux so route guards pass (see demo-seed),
 *   - a global fetch interceptor returns mock fixtures (see demo-backend),
 *   - every screen renders with dummy data so clients can explore freely.
 *
 * No real OAuth, accounts, or PII — everything is mocked. A single welcome modal
 * greets the visitor; there is no guided tour.
 *
 * Demo mode is determined PURELY by the URL: the whole app runs under the
 * `/demo` basename, so the browser path is always `/demo/...` while in the demo
 * (even after client-side navigation). This keeps the demo and the real app at
 * `/` fully separate — a non-`/demo` URL is never treated as demo, so the mock
 * backend and seeded session can't bleed into the real app in the same tab.
 */
const MODE_KEY = 'tara_demo_mode';
const WELCOME_KEY = 'tara_demo_welcomed';

const safeSession = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** True when the current URL is under /demo — i.e. running the simulated demo. */
export const isDemoMode = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    return path === '/demo' || path.startsWith('/demo/');
  } catch {
    return false;
  }
};

export const enableDemoMode = (): void => {
  safeSession()?.setItem(MODE_KEY, '1');
};

export const disableDemoMode = (): void => {
  const s = safeSession();
  s?.removeItem(MODE_KEY);
  s?.removeItem(WELCOME_KEY);
};

/* ---- one-time welcome modal ---- */

/** Whether the welcome modal has already been shown this session. */
export const hasWelcomed = (): boolean =>
  safeSession()?.getItem(WELCOME_KEY) === '1';

export const markWelcomed = (): void => {
  safeSession()?.setItem(WELCOME_KEY, '1');
};
